import { useLazyQuery, useMutation } from '@apollo/client';
import { CREATE_VALUE_INFO, GET_VALUE_LIST_BY_TYPE, UPDATE_VALUE_INFO, UPDATE_VALUE_ORDERS } from '../../../graphql/valuelist-gql';
import { Action } from '../../../types/reducer-action';
import { ICreateValueInfoResponse, IUpdateValueInfoResponse, IValueInfo, IValueInfoInput, IValueListByTypeResponse } from '../../../types/valuelist';
import { DataEntryEvent } from '../../common/data-entry-reducer';
import { useValueListDispatch } from './value-list-manager-context';

const sortData = (data?: IValueInfo[]) => {
  if (!data || data.length === 0) return [];

  const group = data[0].type;
  return [...data].sort((a, b) => {
    if (group === 'CITY') { // sort by code
      return a.code.localeCompare(b.code);
    } else if (!a.displayOrder && !b.displayOrder) {
      return a.ts.localeCompare(b.ts); // newest last
    } else if (!a.displayOrder) { // sort by displayOrder
      return 1; // null last
    } else if (!b.displayOrder) {
      return -1; // null last
    } else {
      return +a.displayOrder - +b.displayOrder;
    }
  })
};

interface ValueListApi {
  loadValueList: (group: string) => void;
  createValueInfo: (input: IValueInfoInput) => Promise<boolean>;
  updateValueInfo: (id: number, input: IValueInfoInput) => Promise<boolean>;
  sortValueList: (valueList: IValueInfo[], updatedValues: IValueInfo[]) => Promise<boolean>;
  busy: boolean;
}

export default function useValueListApi(): ValueListApi {
  const dispatch = useValueListDispatch();

  const [load, { loading }] = useLazyQuery<IValueListByTypeResponse>(GET_VALUE_LIST_BY_TYPE);

  const [create, { loading: creating }] = useMutation<ICreateValueInfoResponse>(CREATE_VALUE_INFO);

  const [update, { loading: updating }] = useMutation<IUpdateValueInfoResponse>(UPDATE_VALUE_INFO);

  const [updateValueOrders, { loading: sorting }] = useMutation<{ updateValueOrders: boolean }>(UPDATE_VALUE_ORDERS);

  const setResults = (results: IValueInfo[] | undefined) =>
    dispatch(new Action(DataEntryEvent.NewDataSet, sortData(results)));

  const loadValueList = (group: string) => {
    dispatch(new Action(DataEntryEvent.SetStateValues, { currentValueGroup: group }));

    setResults(undefined);

    group && load({
      variables: {
        type: group
      }
    }).then(response => setResults(response.data?.getValueListByType));
  };

  const createValueInfo = (input: IValueInfoInput) =>
    create({
      variables: {
        input: input
      },
      update: (cache, { data }) => {
        const createResult = data?.createValueInfo;
        if (!createResult) return;

        const results = cache.readQuery<IValueListByTypeResponse>({
          query: GET_VALUE_LIST_BY_TYPE,
          variables: { type: input.type }
        })?.getValueListByType ?? [];

        const newResults = [...results, createResult];

        cache.writeQuery({
          query: GET_VALUE_LIST_BY_TYPE,
          data: { getValueListByType: newResults },
          variables: { type: input.type }
        });

        dispatch(new Action(DataEntryEvent.NewDataSet, sortData(newResults)));
      }
    }).then(response => !!response.data?.createValueInfo);

  const updateValueInfo = (id: number, input: IValueInfoInput) =>
    update({
      variables: {
        id: id,
        input: input
      }
    }).then(response => {
      const result = response?.data?.updateValueInfo;
      dispatch(new Action(DataEntryEvent.UpdateData, result));
      return !!result;
    });

  const sortValueList = async (valueList: IValueInfo[], updatedValues: IValueInfo[]) => {
    if (updatedValues.length === 0) return true;

    const group = updatedValues[0].type;

    const newEntryList = valueList?.map(a => updatedValues.find(b => b.id === a.id) ?? a);

    return updateValueOrders({
      variables: {
        inputs: updatedValues.map(v => ({
          id: +v.id,
          seq: +v.displayOrder
        }))
      },
      update: (cache, { data }) => {
        data?.updateValueOrders &&
          cache.writeQuery({
            query: GET_VALUE_LIST_BY_TYPE,
            data: { getValueListByType: newEntryList },
            variables: { type: group }
          });
      }
    }).then(response => {
      const ok = !!response.data?.updateValueOrders;
      ok && loadValueList(group);
      return ok;
    });
  };

  const busy = loading || creating || updating || sorting;

  return {
    loadValueList,
    createValueInfo,
    updateValueInfo,
    sortValueList,
    busy
  };
}
